'use client';

import { Box, Alert } from '@mui/material';
import { DataTable, type DataTableColumn } from '@/shared/ui/data/DataTable';
import { Pagination } from '@/shared/ui/navigation/Pagination';
import { PlannerTableSearch } from './PlannerTableSearch';
import { usePlannerSearch, usePlannerPagination } from '@/features/planner/search-planners';
import { useDataTableTheme } from '@/shared/ui/data/DataTable/theme/useDataTableTheme';
import type { PlannerView, PlannerSearchView } from '@/entities/planner';
import type { SimpleSearchQuery } from '@/shared/ui/search/SimpleSearch/types/search.types';
import { useEffect, useMemo, useCallback, useRef } from 'react';
import { DataTableThemeTokens } from '@/shared/ui/data/DataTable/theme';
import { TabContext } from '@/entities/tab/model/tab.types';
import { useUpdateTabParams } from '@/features/tab/tab-history';
import { defaultQuery, convertSimpleToPlannerQuery } from '../model/search';
import type { PlannerSearchQuery } from '@/entities/planner';

const transportLabel: Record<string, string> = {
  car: '자차',
  pubtrans: '대중교통',
  public_transport: '대중교통',
};
const employmentLabel: Record<string, string> = {
  contract: '계약직',
  freelancer: '프리랜서',
};

function getTransportLabel(value: string): string {
  return value ? (transportLabel[value.toLowerCase()] ?? value) : value;
}
function getEmploymentLabel(value: string): string {
  return value ? (employmentLabel[value.toLowerCase()] ?? value) : value;
}

interface PlannerListTableProps {
  pageSize?: number;
  onRowClick?: (planner: PlannerView) => void;
  tabContext?: TabContext;
}

function resolveDataTableColumns(tokens: DataTableThemeTokens): DataTableColumn[] {
  return [
    {
      id: 'name',
      label: '이름',
      type: 'string',
      width: '120px',
      link: {
        to: (row) =>
          `/workspace/planners/${row.id}${row.name ? `?displayName=${encodeURIComponent(row.name)}` : ''}`,
      },
    },
    {
      id: 'phone',
      label: '연락처',
      type: 'string',
      width: '140px',
    },
    {
      id: 'transportationType',
      label: '이동수단',
      type: 'string',
      width: '100px',
    },
    {
      id: 'employmentType',
      label: '고용형태',
      type: 'string',
      width: '100px',
    },
    {
      id: 'misoPartnerId',
      label: 'Miso Partner ID',
      type: 'string',
      width: '120px',
    },
  ];
}

function resolveDataTableRows(data: PlannerSearchView | null) {
  if (!data?.items) return [];
  return data.items.map((planner) => ({
    id: planner.id,
    name: planner.name,
    phone: planner.phone,
    transportationType: getTransportLabel(planner.transportationType),
    employmentType: getEmploymentLabel(planner.employmentType),
    misoPartnerId: planner.misoPartnerId,
    _planner: planner,
  }));
}

export function PlannerListTable({ pageSize = 20, onRowClick, tabContext }: PlannerListTableProps) {
  const { tokens } = useDataTableTheme();
  const { updateTabParams } = useUpdateTabParams();

  const tabParams = tabContext?.params;
  const handleTabParamsUpdate = (params: Record<string, unknown>) => {
    if (tabContext?.tabId) {
      updateTabParams(tabContext.tabId, params);
    }
  };

  const { data, loading, error, search } = usePlannerSearch();
  const lastQueryRef = useRef<PlannerSearchQuery | null>(null);

  const executeSearch = useCallback(
    (event: { cursor?: string | null } | string | null) => {
      const cursorValue: string | null =
        typeof event === 'object' && event !== null && 'cursor' in event
          ? (event.cursor ?? null)
          : typeof event === 'string'
            ? event
            : null;
      const cursor: string | undefined = cursorValue ?? undefined;
      const baseQuery = lastQueryRef.current ?? defaultQuery(pageSize);
      const queryWithCursor: PlannerSearchQuery = { ...baseQuery, pageSize, cursor };
      lastQueryRef.current = queryWithCursor;
      search(queryWithCursor);
    },
    [search, pageSize]
  );

  const { cursor, hasPreviousPage, hasNextPage, setCursorAndIndex } = usePlannerPagination({
    data,
    onPageChange: (newCursor) => {
      executeSearch(newCursor);
    },
    initialPageSize: pageSize,
    tabParams,
    onTabParamsUpdate: handleTabParamsUpdate,
  });

  useEffect(() => {
    executeSearch(cursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const handleSearch = useCallback(
    (simpleQuery: SimpleSearchQuery) => {
      setCursorAndIndex(null, 1);
      const plannerQuery = convertSimpleToPlannerQuery(simpleQuery, undefined);
      plannerQuery.pageSize = pageSize;
      lastQueryRef.current = plannerQuery;
      search(plannerQuery);
    },
    [search, pageSize, setCursorAndIndex]
  );

  const columns: DataTableColumn[] = useMemo(() => resolveDataTableColumns(tokens), [tokens]);
  const rows = useMemo(() => resolveDataTableRows(data), [data]);

  const handleRowClick = (row: Record<string, unknown>) => {
    const planner = row._planner as PlannerView | undefined;
    if (planner) onRowClick?.(planner);
  };

  const initialSimpleQuery: SimpleSearchQuery | undefined = undefined;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        <PlannerTableSearch onSearch={handleSearch} initialQuery={initialSimpleQuery} />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        {!error && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {data && data.items.length > 0 ? (
              <>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <DataTable
                    columns={columns}
                    rows={rows}
                    onRowClick={handleRowClick}
                    stickyHeader
                  />
                </Box>

                <Box
                  sx={{ mt: 2, display: 'flex', justifyContent: 'center', flexShrink: 0, pb: 2 }}
                >
                  <Pagination
                    initialCursor={cursor}
                    initialPageSize={pageSize}
                    hasPreviousPage={hasPreviousPage}
                    hasNextPage={hasNextPage}
                    nextCursor={data.nextCursor}
                    onPageChange={executeSearch}
                    tabContext={tabContext}
                  />
                </Box>
              </>
            ) : !loading && data && data.items.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>검색 결과가 없습니다.</Box>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
}
